import { useEffect, useState, useRef, useCallback } from "react";
import { useLocation } from "react-router-dom";
import RightModal from "../components/templates/RightModal";
import { getLeadsList, selectLeadsData, selectLeadsLoading, deleteLead } from "../redux/leads/leadsSlice";
import DynamicIcon from "../components/DynamicIcon";
import LoadingSection from "../components/LoadingSection";
import { selectAccountData } from "../redux/main/accountSlice";
import { resetRefreshNow, selectRightModalId, selectRightModalIsOpen, selectRightModalIsRefreshNow, selectRightModalmode, selectRightModalData, setModal, setHasToRefresh } from "../redux/rightModalSlice";
import { useDispatch, useSelector } from "react-redux";
import DynamicTitle from "../components/DynamicTitle";
import { useColorPalette } from '../hooks/useColorPalette';
import ListPagination from "../components/templates/ListPagination";
import AddEditLead from "../components/leadsPage/AddEditLead";
import QuickAddProspect from "../components/leadsPage/QuickAddProspect";
import AddEditSystem from "../components/systemsPage/AddEditSystem";
import AddEditAddress from "../components/customersPage/AddEditAddress";
import ViewLead from "../components/leadsPage/ViewLead";
import { getLeadDetails, resetLeadDetails } from "../redux/leads/leadsSlice";

const DEFAULT_LEAD_STATUSES = [
    { label: "Nuova", code: "nuova", color: "#3B82F6" },
    { label: "Contattata", code: "contattata", color: "#F59E0B" },
    { label: "Preventivo", code: "preventivo", color: "#8B5CF6" },
    { label: "Vinta", code: "vinta", color: "#10B981" },
    { label: "Persa", code: "persa", color: "#EF4444" },
];

export default function LeadsList() {
    const dispatch = useDispatch();
    const location = useLocation();
    const palette = useColorPalette();
    const leadsList = useSelector(selectLeadsData);
    const leadsLoading = useSelector(selectLeadsLoading);
    const accountData = useSelector(selectAccountData);
    const token = localStorage.getItem('installamelo-auth-token');
    const [searchTerm, setSearchTerm] = useState('');
    const [statusFilter, setStatusFilter] = useState('');
    const [userFilter, setUserFilter] = useState('');
    const [employeesList, setEmployeesList] = useState([]);
    const [dataTableKey, setDataTableKey] = useState(Date.now());
    const tableRef = useRef(null);
    const prefillHandled = useRef(false);

    // RightModal gestita tramite Redux
    const rightModalIsOpen = useSelector(selectRightModalIsOpen);
    const rightModalMode = useSelector(selectRightModalmode);
    const rightModalLeadId = useSelector(selectRightModalId);
    const rightModalData = useSelector(selectRightModalData);

    // Stati pipeline dall'account o default
    const leadStatuses = accountData?.settings?.leadStatus || DEFAULT_LEAD_STATUSES;

    // Intercetta prefill da navigazione (es. bottone "Crea Lead" da scheda cliente)
    useEffect(() => {
        if (location.state?.openNewLead && !prefillHandled.current) {
            prefillHandled.current = true;
            dispatch(setModal({
                mode: 'add',
                data: { prefillFromCustomer: location.state.prefillFromCustomer }
            }));
            // Pulisci lo state per evitare riaperture
            window.history.replaceState({}, document.title);
        }
    }, [location.state, dispatch]);

    // Carica lista collaboratori per il filtro
    const loadEmployees = useCallback(async () => {
        if (!accountData?._id || !token) return;
        try {
            const res = await fetch(
                `${import.meta.env.VITE_API_BASE_URL}/users/getEmployeesList?accountId=${accountData._id}`,
                { headers: { 'auth-token': token } }
            );
            const data = await res.json();
            setEmployeesList(Array.isArray(data) ? data : (data.data || []));
        } catch { /* ignore */ }
    }, [accountData, token]);

    useEffect(() => {
        loadEmployees();
    }, [loadEmployees]);

    const handlePageUpdate = (start, length) => {
        dispatch(getLeadsList({
            accountId: accountData?._id,
            token,
            search: searchTerm.length >= 3 ? searchTerm : '',
            start,
            length,
            statusCode: statusFilter,
            userId: userFilter
        }));
        dispatch(resetRefreshNow());
    };

    useEffect(() => {
        if (accountData?._id) {
            dispatch(getLeadsList({
                accountId: accountData._id,
                token,
                search: searchTerm.length >= 3 ? searchTerm : '',
                start: 0,
                length: 10,
                statusCode: statusFilter,
                userId: userFilter
            }));
            dispatch(resetRefreshNow());
            setDataTableKey(Date.now());
        }
    }, [dispatch, accountData, token, searchTerm, statusFilter, userFilter]);

    // Carica i dettagli della lead quando il modale viene aperto
    useEffect(() => {
        if (rightModalIsOpen && rightModalLeadId && (rightModalMode === 'edit' || rightModalMode === 'view')) {
            dispatch(getLeadDetails({
                leadId: rightModalLeadId,
                token
            }));
        }
    }, [rightModalIsOpen, rightModalLeadId, rightModalMode, dispatch, token]);

    // Reset dei dettagli lead quando si chiude il modale
    useEffect(() => {
        if (!rightModalIsOpen) {
            dispatch(resetLeadDetails());
        }
    }, [rightModalIsOpen, dispatch]);

    // Aggiorna la tabella quando il modal viene chiuso
    useEffect(() => {
        if (!rightModalIsOpen && dataTableKey) {
            setDataTableKey(Date.now());
        }
    }, [rightModalIsOpen]);

    // Refresh dei dati quando isRefreshNow si attiva
    const rightModalIsRefreshNow = useSelector(selectRightModalIsRefreshNow);
    useEffect(() => {
        if (rightModalIsRefreshNow) handlePageUpdate(0, 10);
    }, [rightModalIsRefreshNow]);

    return (
        <div className="page">
            {leadsLoading && <LoadingSection isFixed />}
            <DynamicTitle title="Lead" />

            <h1>Lead</h1>
            <div className="box">
                {/* Barra di ricerca e filtri */}
                <div className="flex flex-wrap justify-between items-center gap-2 mb-4">
                    <div className="relative w-96">
                        <div className="absolute inset-y-0 start-0 flex items-center ps-3 pointer-events-none">
                            <DynamicIcon iconClass="fa-solid fa-search" />
                        </div>
                        <input
                            type="text"
                            className="bg-gray-50 border border-gray-300 text-gray-900 text-sm rounded-lg focus:ring-blue-500 focus:border-blue-500 block w-full ps-10 p-2.5"
                            placeholder="Cerca lead..."
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                        />
                        {searchTerm && (
                            <button
                                className="absolute inset-y-0 end-0 flex items-center pe-3"
                                onClick={() => setSearchTerm('')}
                            >
                                <DynamicIcon iconClass="fa-solid fa-times" />
                            </button>
                        )}
                    </div>

                    {/* Filtri */}
                    <div className="flex gap-2 flex-wrap">
                        {/* Filtro per stato */}
                        <select
                            className="bg-gray-50 border border-gray-300 text-gray-900 text-sm rounded-lg focus:ring-blue-500 focus:border-blue-500 p-2.5"
                            value={statusFilter}
                            onChange={(e) => setStatusFilter(e.target.value)}
                        >
                            <option value="">Tutti gli stati</option>
                            {leadStatuses.map((status, index) => (
                                <option key={`status-filter-${index}`} value={status.code}>{status.label}</option>
                            ))}
                        </select>

                        {/* Filtro per collaboratore */}
                        <select
                            className="bg-gray-50 border border-gray-300 text-gray-900 text-sm rounded-lg focus:ring-blue-500 focus:border-blue-500 p-2.5"
                            value={userFilter}
                            onChange={(e) => setUserFilter(e.target.value)}
                        >
                            <option value="">Tutti i collaboratori</option>
                            {employeesList.map((emp) => (
                                <option key={emp._id} value={emp._id}>
                                    {[emp.name, emp.surname].filter(Boolean).join(' ')}
                                </option>
                            ))}
                        </select>

                        {/* Bottone Nuova Lead */}
                        <button
                            className="primary"
                            onClick={() => dispatch(setModal({ mode: 'add' }))}
                        >
                            <DynamicIcon iconClass="fa-solid fa-plus" />
                            Nuova Lead
                        </button>
                    </div>
                </div>

                {/* Lista lead */}
                <div className="w-full my-4" ref={tableRef}>
                    {leadsLoading || !accountData?._id ? (
                        <LoadingSection />
                    ) : (
                        <ListPagination
                            initTotalElements={leadsList?.recordsFiltered || 0}
                            onPageChange={handlePageUpdate}
                        >
                            <div className="w-full text-sm flex flex-col gap-2">
                                {/* Headers */}
                                <div className="w-full flex justify-between font-bold rounded-t-lg" style={{ backgroundColor: palette.tableHeaderBg, color: palette.tableHeaderText }}>
                                    <div className="px-6 py-4 w-[5%]">TIPO</div>
                                    <div className="px-6 py-4 w-[18%]">LEAD</div>
                                    <div className="px-6 py-4 w-[15%]">CLIENTE/PROSPECT</div>
                                    <div className="px-6 py-4 w-[12%]">IMPIANTO</div>
                                    <div className="px-6 py-4 w-[10%]">VALORE</div>
                                    <div className="px-6 py-4 w-[13%]">ASSEGNATA A</div>
                                    <div className="px-6 py-4 w-[10%]">STATO</div>
                                    <div className="px-6 py-4 w-[17%] flex justify-end">AZIONI</div>
                                </div>

                                {/* Rows */}
                                {
                                    leadsList?.data?.map((lead, index) => {
                                        const isEven = index % 2 === 0;
                                        const rowBgColor = isEven ? palette.tableRowLight : palette.tableRowDark;
                                        const rowTextColor = isEven ? palette.textOnTableRowLight : palette.textOnTableRowDark;

                                        return (
                                            <LeadLine
                                                key={lead._id}
                                                leadData={lead}
                                                index={index}
                                                rowBgColor={rowBgColor}
                                                rowTextColor={rowTextColor}
                                                rowHoverBgColor={palette.highlightBg}
                                                rowHoverTextColor={palette.textOnHighlight}
                                            />
                                        );
                                    })
                                }

                                {/* Messaggio quando non ci sono dati */}
                                {(!leadsList?.data || leadsList.data.length === 0) && !leadsLoading && (
                                    <div className="w-full py-4 text-center text-gray-500">
                                        Nessuna lead trovata
                                    </div>
                                )}
                            </div>
                        </ListPagination>
                    )}
                </div>
            </div>

            {/* RIGHT MODAL */}
            {
                rightModalIsOpen && (
                    <RightModal>
                        {(rightModalMode === 'add' || rightModalMode === 'edit') && (
                            <AddEditLead mode={rightModalMode} />
                        )}
                        {rightModalMode === 'add_prospect' && (
                            <QuickAddProspect />
                        )}
                        {rightModalMode === 'add_system' && (
                            <AddEditSystem
                                systemId={null}
                                preselectedCustomer={rightModalData?.preselectedCustomer || null}
                            />
                        )}
                        {rightModalMode === 'add_address' && (
                            <AddEditAddress
                                customerId={rightModalData?.customerId}
                                customerData={rightModalData?.customerData}
                            />
                        )}
                        {
                            rightModalMode === 'view' && rightModalLeadId && (
                                <ViewLead />
                            )
                        }
                    </RightModal>
                )
            }
        </div>
    );
}

// Componente inline per la riga della lead
function LeadLine({ leadData, index, rowBgColor, rowTextColor, rowHoverBgColor, rowHoverTextColor }) {
    const [isHovered, setIsHovered] = useState(false);
    const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
    const dispatch = useDispatch();
    const token = localStorage.getItem('installamelo-auth-token');

    const handleRowClick = () => {
        dispatch(setModal({
            mode: 'edit',
            id: leadData._id
        }));
    };

    const handleViewClick = (e) => {
        e.stopPropagation();
        dispatch(setModal({
            mode: 'view',
            id: leadData._id
        }));
    };

    const handleEditClick = (e) => {
        e.stopPropagation();
        dispatch(setModal({
            mode: 'edit',
            id: leadData._id
        }));
    };

    const handleDeleteClick = (e) => {
        e.stopPropagation();
        setShowDeleteConfirm(true);
    };

    const confirmDelete = (e) => {
        e.stopPropagation();
        if (leadData?._id) {
            dispatch(deleteLead({
                leadId: leadData._id,
                token
            })).then((result) => {
                if (!result.error) {
                    dispatch(setHasToRefresh());
                    setShowDeleteConfirm(false);
                }
            });
        }
    };

    const getLeadDisplayName = () => {
        return leadData.description || leadData.linkedCustomerLabel || leadData.code || '--';
    };

    const getSystemLabel = () => {
        return leadData.linkedMaintenanceLabel || '--';
    };

    const formatCurrency = (value) => {
        if (!value && value !== 0) return '--';
        return new Intl.NumberFormat('it-IT', { style: 'currency', currency: 'EUR' }).format(value);
    };

    const getStatusBadge = () => {
        const color = leadData.statusColor || '#6B7280';
        return (
            <span
                className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium"
                style={{ backgroundColor: color + '20', color: color }}
            >
                {leadData.statusLabel || 'N/D'}
            </span>
        );
    };

    const getAssignedUsers = () => {
        if (!leadData.assignedUsers || leadData.assignedUsers.length === 0) return '--';
        return (
            <div className="flex flex-wrap gap-1">
                {leadData.assignedUsers.map((u, i) => (
                    <span
                        key={i}
                        className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium"
                        style={{
                            backgroundColor: (u.userColor || '#6B7280') + '20',
                            color: u.userColor || '#6B7280'
                        }}
                    >
                        {u.userLabel}
                    </span>
                ))}
            </div>
        );
    };

    return (
        <>
            <div
                style={{
                    backgroundColor: isHovered ? rowHoverBgColor : rowBgColor,
                    color: isHovered ? rowHoverTextColor : rowTextColor,
                    transition: 'background-color 0.15s ease'
                }}
                className="w-full flex justify-between cursor-pointer rounded-lg border-b border-gray-100"
                onClick={handleRowClick}
                onMouseEnter={() => setIsHovered(true)}
                onMouseLeave={() => setIsHovered(false)}
            >
                {/* Tipo */}
                <div className="px-6 py-4 w-[5%] flex items-center">
                    <DynamicIcon iconClass={leadData.business ? "fa-solid fa-building" : "fa-solid fa-user"} />
                </div>

                {/* Lead */}
                <div className="px-6 py-4 w-[18%]">
                    <div className="font-semibold">{getLeadDisplayName()}</div>
                    <div className="text-xs text-gray-500">{leadData.code || ''}</div>
                </div>

                {/* Cliente/Prospect */}
                <div className="px-6 py-4 w-[15%]">
                    {leadData.linkedCustomerLabel ? (
                        <div className="flex items-center gap-1">
                            <span className="text-sm truncate">{leadData.linkedCustomerLabel}</span>
                            {leadData.linkedCustomerIsProspect ? (
                                <span className="inline-flex items-center px-1.5 py-0.5 rounded-full text-xs font-medium bg-orange-100 text-orange-700 whitespace-nowrap">P</span>
                            ) : (
                                <span className="inline-flex items-center px-1.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-700 whitespace-nowrap">C</span>
                            )}
                        </div>
                    ) : (
                        <span className="text-gray-400 text-sm">--</span>
                    )}
                </div>

                {/* Impianto */}
                <div className="px-6 py-4 w-[12%] truncate text-sm">
                    {getSystemLabel()}
                </div>

                {/* Valore */}
                <div className="px-6 py-4 w-[10%] font-medium">
                    {formatCurrency(leadData.economicValue)}
                </div>

                {/* Assegnata a */}
                <div className="px-6 py-4 w-[13%]">
                    {getAssignedUsers()}
                </div>

                {/* Stato */}
                <div className="px-6 py-4 w-[10%]">
                    {getStatusBadge()}
                </div>

                {/* Azioni */}
                <div className="px-6 py-4 w-[17%] flex justify-end items-center space-x-2">
                    <button
                        className="p-1 hover:bg-blue-100 rounded-full"
                        onClick={handleViewClick}
                        title="Visualizza"
                    >
                        <DynamicIcon iconClass="fa-solid fa-eye" />
                    </button>
                    <button
                        className="p-1 hover:bg-blue-100 rounded-full"
                        onClick={handleEditClick}
                        title="Modifica"
                    >
                        <DynamicIcon iconClass="fa-solid fa-edit" />
                    </button>
                    <button
                        className="p-1 hover:bg-red-100 rounded-full text-red-500"
                        onClick={handleDeleteClick}
                        title="Elimina"
                    >
                        <DynamicIcon iconClass="fa-solid fa-trash" />
                    </button>
                </div>
            </div>

            {/* Dialog conferma eliminazione */}
            {showDeleteConfirm && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50" onClick={(e) => e.stopPropagation()}>
                    <div className="bg-white rounded-lg p-6 max-w-md w-full">
                        <h3 className="text-xl font-bold mb-4">Conferma Eliminazione</h3>
                        <p className="mb-6">
                            Sei sicuro di voler eliminare la lead "{getLeadDisplayName()}"?
                            Questa azione non può essere annullata.
                        </p>
                        <div className="flex justify-end space-x-3">
                            <button
                                className="secondary"
                                onClick={(e) => { e.stopPropagation(); setShowDeleteConfirm(false); }}
                            >
                                Annulla
                            </button>
                            <button
                                className="danger"
                                onClick={confirmDelete}
                            >
                                <DynamicIcon iconClass="fas fa-trash" />
                                Elimina
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </>
    );
}
